import React from 'react';
import { StudyPlan } from '../../types/onboarding';
import { Clock, Calendar, Target, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
    plan: StudyPlan;
}

const PlanPreview: React.FC<Props> = ({ plan }) => {
    const navigate = useNavigate();

    const [isNavigating, setIsNavigating] = React.useState(false);

    const handleStartMission = () => {
        // Navigate to the deep link of the first task
        if (plan.todayMission.tasks.length > 0) {
            const task = plan.todayMission.tasks[0];
            setIsNavigating(true);

            // Parse the deepLink
            if (task.deepLink.startsWith('/')) {
                // It's a relative path - use navigate
                navigate(task.deepLink);
            } else {
                // Fallback to home
                navigate('/');
            }
        } else {
            navigate('/');
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Plan Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-2xl flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Plán</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{plan.template === 'sprinter' ? '1-2 týdny' : plan.template === 'planner' ? '3-8 týdnů' : 'Flexibilní'}</p>
                    </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-2xl flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                        <Target className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Intenzita</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{plan.cadence.daysPerWeek}× týdně</p>
                    </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-2xl flex items-center gap-3">
                    <div className="p-2 bg-orange-500/10 rounded-lg">
                        <Clock className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Denně</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{plan.cadence.minutesPerDay} min</p>
                    </div>
                </div>
            </div>

            {/* Focus Area */}
            <div className="p-6 bg-brand-orange/5 border border-brand-orange/10 rounded-2xl">
                <h3 className="text-sm font-bold text-brand-orange uppercase tracking-wider mb-3">Zaměření</h3>
                <p className="text-gray-700 dark:text-gray-300">
                    {plan.focusSplit.osp ? `OSP: Logika (${plan.focusSplit.osp.logic}%), Text (${plan.focusSplit.osp.reading}%), Tabulky (${plan.focusSplit.osp.data}%)` : ''}
                    {plan.focusSplit.osp && plan.focusSplit.zsv ? ' • ' : ''}
                    {plan.focusSplit.zsv ? `ZSV: Vyvážené studium všech témat` : ''}
                </p>
            </div>

            {/* Today's Mission */}
            <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Dnešní mise ({plan.todayMission.estimatedMinutes} min)</h3>
                <div className="space-y-3">
                    {plan.todayMission.tasks.map((task, idx) => (
                        <div
                            key={task.id}
                            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm"
                        >
                            <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-bold text-gray-500">
                                    {idx + 1}
                                </span>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{task.label}</p>
                            </div>
                            <span className="text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                {task.minutes} min
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <button
                    onClick={handleStartMission}
                    disabled={isNavigating}
                    className="w-full bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 group"
                >
                    {isNavigating ? (
                        <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Načítání...
                        </>
                    ) : (
                        <>
                            Spustit dnešní misi
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                    )}
                </button>
                <button
                    onClick={() => navigate('/')}
                    className="w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                    Zobrazit celý plán v dashboardu
                </button>
            </div>
        </div>
    );
};

export default PlanPreview;
